import type { ComponentType } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppRefresh } from '../../hooks/useAppRefresh';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Chart, registerables } from 'chart.js';
import type { ChartConfiguration } from 'chart.js';
import PageHeader from '../../components/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import weatherService, {
  type ForecastRange,
  type ForecastDay,
  type FarmWeatherOverview,
  type ModelCurrentConditions,
} from '../../services/weather';
import RadarCard from '../../components/weather/RadarCard';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import weatherStationService, {
  type WeatherStation,
  type WeatherObservation,
  type WeatherStationObservationsResponse,
} from '../../services/weatherStations';
import { requestLocationWithDisclosure } from '../../services/locationGate';
import {
  Thermometer, Droplets, Wind, Umbrella, Sun, Gauge, CloudSun,
  Radio, Sprout, Leaf, Wifi, WifiOff, Loader2, Plus, History, Pencil,
  Cloud, CloudRain, CloudSnow, CloudFog, CloudLightning, MapPin, Snowflake, Bug,
} from 'lucide-react';
import '../../styles/GlobalPageStyles.css';
import { toNum, type Numeric } from '@/utils/numeric';

Chart.register(...registerables);

type TimeRange = '24h' | '7d' | '30d';

function degToCompass(deg: number | null): string {
  if (deg == null) return '';
  const dirs = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
  return dirs[Math.round(deg / 22.5) % 16];
}

function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h = sinDLat * sinDLat
    + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180)
    * sinDLng * sinDLng;
  return 2 * R * Math.asin(Math.sqrt(h));
}

const SNAP_RADIUS_KM = 5;
// A station reading older than this is treated as offline and the model
// estimate stands in — same threshold the farms-overview endpoint uses.
const STATION_FRESH_MINUTES = 60;
type LocationKey = 'gps' | `farm:${number}`;

function formatAgo(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function num(v: Numeric | undefined, digits = 1): string {
  const n = toNum(v);
  return n == null ? '—' : n.toFixed(digits);
}

function pickForecastIcon(code: number | null) {
  if (code == null) return Cloud;
  if (code >= 95) return CloudLightning;
  if (code >= 71 && code <= 77) return CloudSnow;
  if (code >= 80 && code <= 86) return CloudRain;
  if (code >= 51 && code <= 67) return CloudRain;
  if (code >= 45 && code <= 48) return CloudFog;
  if (code === 0) return Sun;
  if (code >= 1 && code <= 3) return CloudSun;
  return Cloud;
}

function formatDayLabel(isoDate: string, idx: number, tz: string): string {
  if (idx === 0) return 'Today';
  if (idx === 1) return 'Tomorrow';
  return formatLocalDateInTz(isoDate, tz, {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

// -----------------------------------------------------------------------------
// StatTile
// -----------------------------------------------------------------------------
function StatTile({
  icon: Icon, label, value, unit, color = 'sky',
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string;
  unit?: string;
  color?: 'sky' | 'blue' | 'indigo' | 'amber' | 'green' | 'rose' | 'slate';
}) {
  const palette: Record<string, string> = {
    sky:    'bg-sky-50 text-sky-700',
    blue:   'bg-blue-50 text-blue-700',
    indigo: 'bg-indigo-50 text-indigo-700',
    amber:  'bg-amber-50 text-amber-700',
    green:  'bg-green-50 text-green-700',
    rose:   'bg-rose-50 text-rose-700',
    slate:  'bg-slate-50 text-slate-700',
  };
  return (
    <div className={`rounded-xl p-4 ${palette[color]}`}>
      <div className="flex items-center gap-2 text-xs font-medium opacity-80">
        <Icon className="h-4 w-4" />
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold">
        {value}{unit && <span className="text-sm font-medium ml-1 opacity-70">{unit}</span>}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// ForecastDayCard
// -----------------------------------------------------------------------------
function ForecastDayCard({ day, idx, tz }: { day: ForecastDay; idx: number; tz: string }) {
  const Icon = pickForecastIcon(day.code);
  return (
    <div className="flex-1 min-w-[120px] rounded-xl bg-gradient-to-b from-sky-50 to-white border border-sky-100 p-3">
      <div className="text-xs font-semibold text-neutral-600 truncate">
        {formatDayLabel(day.date, idx, tz)}
      </div>
      <div className="flex items-center justify-center my-2">
        <Icon className="h-8 w-8 text-sky-600" />
      </div>
      <div className="text-sm font-semibold text-neutral-800 truncate">{day.conditions}</div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-lg font-bold text-neutral-900">{num(day.temp_max, 0)}°</span>
        <span className="text-sm text-neutral-500">/ {num(day.temp_min, 0)}°</span>
      </div>
      <div className="mt-1 flex items-center gap-1 text-xs text-indigo-600">
        <Umbrella className="h-3 w-3" /> {num(day.precip_mm, 1)} mm
      </div>
      <div className="flex items-center gap-1 text-xs text-sky-600">
        <Wind className="h-3 w-3" /> {num(day.wind_max_kph, 0)} km/h
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// CurrentConditionsCard — one layout for a station reading or a model estimate
// -----------------------------------------------------------------------------
type CurrentReading = Pick<WeatherObservation,
  'temp_c' | 'humidity' | 'wind_kph' | 'wind_dir' | 'wind_gust_kph' | 'rain_mm_hour'
  | 'rain_mm_day' | 'solar_wm2' | 'pressure_hpa' | 'dew_point_c' | 'feels_like_c' | 'uv'>;

function CurrentConditionsCard({
  reading, source,
}: {
  reading: CurrentReading;
  source:
    | { kind: 'station'; stationName: string; observedAt: string }
    | { kind: 'model'; conditions: string; elevationM: number | null; staleStation: { name: string; lastSeen: string | null } | null };
}) {
  const isStation = source.kind === 'station';
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          {isStation
            ? <Radio className="h-5 w-5 text-sky-600" />
            : <CloudSun className="h-5 w-5 text-sky-600" />}
          Current conditions
        </CardTitle>
        {isStation ? (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-green-50 px-2 py-0.5 font-semibold text-green-700">Measured on-farm</span>
            <span className="text-neutral-600 break-words min-w-0">
              {source.stationName} · updated {formatAgo(source.observedAt)}
            </span>
          </div>
        ) : (
          <div className="space-y-1 text-xs">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-800">Modelled estimate</span>
              <span className="text-neutral-600">
                Open-Meteo{source.elevationM != null ? ` · grid cell at ${Math.round(source.elevationM)} m` : ''} · {source.conditions}
              </span>
            </div>
            <p className="text-neutral-500">
              {source.staleStation
                ? `${source.staleStation.name} ${source.staleStation.lastSeen ? `last reported ${formatAgo(source.staleStation.lastSeen)}` : 'has not reported yet'}, so these are weather-model figures for this location instead. `
                : 'No on-farm station here, so these are weather-model figures for this location. '}
              A model cell is a few kilometres wide — wind, rain and frost on the block can differ.
            </p>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile icon={Thermometer} label="Temperature" value={num(reading.temp_c, 1)} unit="°C" color="sky" />
          <StatTile icon={Droplets} label="Humidity" value={num(reading.humidity, 0)} unit="%" color="blue" />
          <StatTile
            icon={Wind} label="Wind"
            value={`${num(reading.wind_kph, 0)} ${degToCompass(reading.wind_dir)}`}
            unit="km/h" color="sky"
          />
          <StatTile icon={Wind} label="Gust" value={num(reading.wind_gust_kph, 0)} unit="km/h" color="slate" />
          <StatTile icon={Umbrella} label="Rain (last hour)" value={num(reading.rain_mm_hour, 1)} unit="mm" color="indigo" />
          <StatTile icon={Umbrella} label="Rain today" value={num(reading.rain_mm_day, 1)} unit="mm" color="indigo" />
          <StatTile icon={Sun} label="Solar" value={num(reading.solar_wm2, 0)} unit="W/m²" color="amber" />
          <StatTile icon={Gauge} label="Pressure" value={num(reading.pressure_hpa, 0)} unit="hPa" color="slate" />
        </div>
        {(reading.dew_point_c != null || reading.feels_like_c != null || reading.uv != null) && (
          <div className="mt-3 flex flex-wrap gap-2 text-sm text-neutral-600">
            {reading.feels_like_c != null && <span>Feels like {num(reading.feels_like_c, 1)}°C</span>}
            {reading.dew_point_c != null && <span>· Dew point {num(reading.dew_point_c, 1)}°C</span>}
            {reading.uv != null && <span>· UV {num(reading.uv, 1)}</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// WeatherPage
// -----------------------------------------------------------------------------
export default function WeatherPage() {
  const { stationId: stationIdParam } = useParams<{ stationId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const farmIdParam = searchParams.get('farm');
  const orgTz = useOrgTimezone();

  const [stations, setStations] = useState<WeatherStation[]>([]);
  const [station, setStation] = useState<WeatherStation | null>(null);
  const [obs, setObs] = useState<WeatherStationObservationsResponse | null>(null);
  const [forecast, setForecast] = useState<ForecastRange | null>(null);
  const [modelCurrent, setModelCurrent] = useState<ModelCurrentConditions | null>(null);
  const [forecastLocation, setForecastLocation] = useState<{ lat: number; lng: number; label: string } | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');
  const [loading, setLoading] = useState(true);
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState<string | null>(null);

  // All farms in the org with their resolved weather snapshot — used both for
  // the location picker and to determine "nearest farm within 5 km" snap on GPS.
  const [allFarms, setAllFarms] = useState<FarmWeatherOverview[]>([]);
  const [selectedKey, setSelectedKey] = useState<LocationKey | null>(null);
  const [gpsCoords, setGpsCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [gpsSnappedFarmId, setGpsSnappedFarmId] = useState<number | null>(null);

  const tempChartRef = useRef<HTMLCanvasElement>(null);
  const rainChartRef = useRef<HTMLCanvasElement>(null);
  const windChartRef = useRef<HTMLCanvasElement>(null);
  const solarChartRef = useRef<HTMLCanvasElement>(null);
  const chartInstances = useRef<Chart[]>([]);

  const [refreshTick, setRefreshTick] = useState(0);
  useAppRefresh(useCallback(() => setRefreshTick(t => t + 1), []));

  // Load stations + farm overview in parallel — tolerate station 403 gracefully
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const [stationsRes, farmsRes] = await Promise.all([
          weatherStationService.list().catch(() => null),
          weatherService.getFarmsOverview(),
        ]);
        if (cancelled) return;

        let stationList: WeatherStation[] = [];
        if (stationsRes?.success && stationsRes.data) {
          setHasSubscription(true);
          stationList = stationsRes.data;
          setStations(stationList);
        } else {
          setHasSubscription(false);
        }

        const farms = farmsRes || [];
        setAllFarms(farms);

        // Seed selectedKey priority: ?farm= query, then :stationId path,
        // then first farm, else GPS.
        if (farmIdParam && farms.some((f) => f.farm_id === Number(farmIdParam))) {
          setSelectedKey(`farm:${Number(farmIdParam)}`);
        } else if (stationIdParam) {
          const s = stationList.find((x) => x.id === Number(stationIdParam));
          if (s?.farm_id) {
            setSelectedKey(`farm:${s.farm_id}`);
          } else if (s) {
            setStation(s);
            setSelectedKey(null);
          }
        } else if (farms.length > 0 && selectedKey == null) {
          setSelectedKey(`farm:${farms[0].farm_id}`);
        } else if (selectedKey == null) {
          setSelectedKey('gps');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationIdParam, farmIdParam, refreshTick]);

  // Drive station + forecastLocation off selectedKey
  useEffect(() => {
    if (!selectedKey) return;

    if (selectedKey.startsWith('farm:')) {
      const farmId = Number(selectedKey.slice('farm:'.length));
      const farm = allFarms.find((f) => f.farm_id === farmId);
      if (!farm) return;
      const matchingStation = farm.station_id
        ? stations.find((s) => s.id === farm.station_id) || null
        : null;
      setStation(matchingStation);
      setGpsSnappedFarmId(null);
      if (farm.lat != null && farm.lng != null) {
        setForecastLocation({ lat: farm.lat, lng: farm.lng, label: farm.farm_name });
      }
      return;
    }

    // GPS — snap to nearest farm if within SNAP_RADIUS_KM
    let cancelled = false;
    requestLocationWithDisclosure({
      timeout: 10000,
      maximumAge: 10 * 60 * 1000,
      enableHighAccuracy: false,
    }).then((loc) => {
      if (cancelled || !loc) return;
      const here = { lat: loc.coords.latitude, lng: loc.coords.longitude };
      setGpsCoords(here);

      let nearest: FarmWeatherOverview | null = null;
      let nearestKm = Infinity;
      for (const f of allFarms) {
        if (f.lat == null || f.lng == null) continue;
        const km = haversineKm(here, { lat: f.lat, lng: f.lng });
        if (km < nearestKm) { nearestKm = km; nearest = f; }
      }

      if (nearest && nearestKm <= SNAP_RADIUS_KM) {
        const matchingStation = nearest.station_id
          ? stations.find((s) => s.id === nearest!.station_id) || null
          : null;
        setStation(matchingStation);
        setGpsSnappedFarmId(nearest.farm_id);
        setForecastLocation({
          lat: nearest.lat!,
          lng: nearest.lng!,
          label: `${nearest.farm_name} (${nearestKm.toFixed(1)} km away)`,
        });
      } else {
        setStation(null);
        setGpsSnappedFarmId(null);
        setForecastLocation({ lat: here.lat, lng: here.lng, label: 'My location' });
      }
    });
    return () => { cancelled = true; };
  }, [selectedKey, allFarms, stations]);

  // Fetch forecast whenever the location resolves
  useEffect(() => {
    if (!forecastLocation) return;
    let cancelled = false;
    weatherService.getForecastRange(forecastLocation.lat, forecastLocation.lng, 7).then((res) => {
      if (!cancelled) setForecast(res);
    });
    return () => { cancelled = true; };
  }, [forecastLocation, refreshTick]);

  const latest: WeatherObservation | null = station?.latest_observation ?? null;
  const stationFresh = latest != null
    && Date.now() - new Date(latest.observed_at).getTime() <= STATION_FRESH_MINUTES * 60 * 1000;

  // Model current conditions wherever there is no fresh station reading
  useEffect(() => {
    setModelCurrent(null);
    if (!forecastLocation || stationFresh) return;
    let cancelled = false;
    weatherService.getCurrent(forecastLocation.lat, forecastLocation.lng).then((res) => {
      if (!cancelled) setModelCurrent(res);
    });
    return () => { cancelled = true; };
  }, [forecastLocation, stationFresh, refreshTick]);

  // Load station observations when a station is selected
  useEffect(() => {
    if (!station) return;
    let cancelled = false;
    async function load() {
      const now = new Date();
      const hrs = timeRange === '24h' ? 24 : timeRange === '7d' ? 24 * 7 : 24 * 30;
      const from = new Date(now.getTime() - hrs * 60 * 60 * 1000);
      const res = await weatherStationService.getObservations(station!.id, from, now, 3000);
      if (cancelled) return;
      if (res?.success && res.data) setObs(res.data);
    }
    load();
    return () => { cancelled = true; };
  }, [station, timeRange, refreshTick]);

  // Draw station history charts
  useEffect(() => {
    chartInstances.current.forEach((c) => c.destroy());
    chartInstances.current = [];

    if (!obs || !obs.observations.length) return;

    const labels = obs.observations.map((o) => new Date(o.observed_at).toLocaleString([], {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    }));
    const isMobile = window.innerWidth < 640;
    const makeLine = (
      canvas: HTMLCanvasElement | null,
      label: string,
      data: Numeric[],
      color: string,
      yMin?: number,
    ): Chart | null => {
      if (!canvas) return null;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      const cfg: ChartConfiguration = {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label,
            data: data.map(toNum),
            borderColor: color,
            backgroundColor: `${color}22`,
            fill: true,
            tension: 0.25,
            pointRadius: 0,
            borderWidth: 2,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: { ticks: { display: !isMobile, maxTicksLimit: 6, autoSkip: true } },
            y: yMin != null ? { min: yMin } : { beginAtZero: false },
          },
          plugins: { legend: { display: false } },
        },
      };
      return new Chart(ctx, cfg);
    };

    const temp = makeLine(tempChartRef.current, 'Temp (°C)', obs.observations.map((o) => o.temp_c), '#0284c7');
    const rain = makeLine(rainChartRef.current, 'Rain (mm/h)', obs.observations.map((o) => o.rain_mm_hour), '#6366f1', 0);
    const wind = makeLine(windChartRef.current, 'Wind (km/h)', obs.observations.map((o) => o.wind_kph), '#0ea5e9', 0);
    const solar = makeLine(solarChartRef.current, 'Solar (W/m²)', obs.observations.map((o) => o.solar_wm2), '#f59e0b', 0);

    [temp, rain, wind, solar].forEach((c) => { if (c) chartInstances.current.push(c); });

    return () => {
      chartInstances.current.forEach((c) => c.destroy());
      chartInstances.current = [];
    };
  }, [obs]);

  const hasStation = Boolean(station);
  const hasSoil = latest && (latest.soil_temp_c_1 != null || latest.soil_moisture_1 != null);
  const hasAir = latest && (latest.pm25 != null || latest.co2 != null);

  // --- Render ---
  if (loading) {
    return (
      <>
        <PageHeader title="Weather" />
        <div className="max-w-6xl mx-auto p-6">
          <div className="flex items-center gap-2 text-neutral-500 mt-6">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading…
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={hasStation ? station!.name : (forecastLocation?.label || 'Weather')}
        subtitle={hasStation
          ? (station!.farm_name || 'On-farm weather station')
          : (selectedKey === 'gps' && gpsSnappedFarmId
              ? 'Forecast at nearest farm (snapped)'
              : 'Open-Meteo forecast')}
      />
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Location picker — always shown */}
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm min-w-0 max-w-full">
            <MapPin className="h-4 w-4 shrink-0 text-neutral-500" />
            <select
              className="min-w-0 max-w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm"
              value={selectedKey ?? ''}
              onChange={(e) => setSelectedKey(e.target.value as LocationKey)}
            >
              <option value="gps">📍 Current location (GPS)</option>
              {allFarms.map((f) => (
                <option key={f.farm_id} value={`farm:${f.farm_id}`}>
                  {f.farm_name}{f.station_id ? ' · station' : ''}
                </option>
              ))}
            </select>
          </label>
          <button
            onClick={() => {
              const farmId = selectedKey?.startsWith('farm:')
                ? Number(selectedKey.slice('farm:'.length))
                : (station?.farm_id ?? gpsSnappedFarmId ?? null);
              navigate(farmId != null ? `/weather/chill?farm=${farmId}` : '/weather/chill');
            }}
            title="Winter-chill accumulation for deciduous fruit (chill portions, Utah, chilling hours)"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-violet-50 text-violet-700 hover:bg-violet-100"
          >
            <Snowflake className="h-4 w-4" /> Winter chill
          </button>
          <button
            onClick={() => {
              const farmId = selectedKey?.startsWith('farm:')
                ? Number(selectedKey.slice('farm:'.length))
                : (station?.farm_id ?? gpsSnappedFarmId ?? null);
              navigate(farmId != null ? `/weather/pests?farm=${farmId}` : '/weather/pests');
            }}
            title="Pest & disease forecasting (black spot, LBAM and more)"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-teal-50 text-teal-700 hover:bg-teal-100"
          >
            <Bug className="h-4 w-4" /> Pest &amp; disease risk
          </button>
          {selectedKey === 'gps' && gpsSnappedFarmId && (
            <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
              Snapped to {allFarms.find((f) => f.farm_id === gpsSnappedFarmId)?.farm_name}
            </span>
          )}
          {selectedKey === 'gps' && !gpsSnappedFarmId && gpsCoords && (
            <span className="text-xs text-sky-700 bg-sky-50 px-2 py-1 rounded-full">
              Open-Meteo · &gt;5 km from any farm
            </span>
          )}
        </div>

        {/* Station status + actions (only when station resolved) */}
        {hasStation && (
          <div className="flex flex-wrap items-center gap-3">
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${station!.last_seen_at ? 'bg-green-50 text-green-700' : 'bg-neutral-100 text-neutral-500'}`}>
              {station!.last_seen_at ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
              {station!.last_seen_at ? `Last seen ${formatAgo(station!.last_seen_at)}` : 'No data yet'}
            </div>
            <div className="flex gap-1 ml-auto items-center">
              <button
                onClick={async () => {
                  if (!station || backfilling) return;
                  setBackfilling(true);
                  setBackfillMsg(null);
                  try {
                    await weatherStationService.triggerBackfill(station.id);
                    setBackfillMsg('Backfill started — history will appear within a minute.');
                  } catch {
                    setBackfillMsg('Backfill request failed.');
                  } finally {
                    setBackfilling(false);
                  }
                }}
                disabled={backfilling}
                title="Re-fetch 30 days of history from Ambient Weather"
                className="ml-1 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-neutral-100 text-neutral-600 hover:bg-neutral-200 disabled:opacity-50"
              >
                {backfilling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <History className="h-3.5 w-3.5" />}
                <span className="hidden sm:inline">Backfill</span>
              </button>
              <button
                onClick={() => navigate('/weather-stations/setup')}
                title="Manage stations — rename, reassign farm, pause or remove"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
              >
                <Pencil className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Manage</span>
              </button>
            </div>
            {backfillMsg && (
              <p className="w-full text-xs text-sky-700 mt-1">{backfillMsg}</p>
            )}
          </div>
        )}

        {/* ================================================================ */}
        {/*  Current conditions — station when fresh, model estimate otherwise */}
        {/* ================================================================ */}
        {stationFresh && latest ? (
          <CurrentConditionsCard
            reading={latest}
            source={{ kind: 'station', stationName: station!.name, observedAt: latest.observed_at }}
          />
        ) : modelCurrent ? (
          <CurrentConditionsCard
            reading={modelCurrent}
            source={{
              kind: 'model',
              conditions: modelCurrent.conditions,
              elevationM: modelCurrent.grid_elevation_m,
              staleStation: station ? { name: station.name, lastSeen: station.last_seen_at } : null,
            }}
          />
        ) : null}

        {/* ================================================================ */}
        {/*  Radar — RainViewer overlay centred on selected location         */}
        {/* ================================================================ */}
        {forecastLocation && (
          <RadarCard
            lat={forecastLocation.lat}
            lng={forecastLocation.lng}
            label={forecastLocation.label}
          />
        )}

        {/* ================================================================ */}
        {/*  7-day forecast (always available via Open-Meteo)                */}
        {/* ================================================================ */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <CloudSun className="h-5 w-5 text-sky-600" /> 7-day forecast
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!forecast && !forecastLocation && (
              <div className="text-sm text-neutral-500">Enable location to see the forecast.</div>
            )}
            {!forecast && forecastLocation && (
              <div className="flex items-center gap-2 text-sm text-neutral-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Fetching forecast…
              </div>
            )}
            {forecast && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {forecast.daily.slice(0, 7).map((d, i) => (
                  <ForecastDayCard key={d.date} day={d} idx={i} tz={orgTz} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ================================================================ */}
        {/*  Next 24h hourly forecast chart (always available)               */}
        {/* ================================================================ */}
        {forecast && forecast.hourly.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Next 24h forecast</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <HourlyMiniChart
                  label="Temperature (°C)"
                  color="#0284c7"
                  data={forecast.hourly.slice(0, 24).map((h) => ({ t: h.time, v: h.temp_c }))}
                />
                <HourlyMiniChart
                  label="Rain (mm)"
                  color="#6366f1"
                  data={forecast.hourly.slice(0, 24).map((h) => ({ t: h.time, v: h.precip_mm }))}
                  yMin={0}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* ================================================================ */}
        {/*  Compact pair banner (no station paired yet)                     */}
        {/* ================================================================ */}
        {!hasStation && (
          <div className="flex flex-wrap items-center gap-3 p-4 rounded-xl bg-sky-50 border border-sky-100">
            <Radio className="h-5 w-5 text-sky-600 shrink-0" />
            <div className="flex-1 min-w-[200px]">
              <span className="text-sm font-semibold text-sky-900">Want hyper-local on-farm data?</span>
              <span className="text-sm text-sky-800 ml-1">
                Pair an Ambient Weather station for live readings and history.
              </span>
              {hasSubscription === false && (
                <span className="text-xs text-sky-700 ml-1">(subscription required)</span>
              )}
            </div>
            <Button onClick={() => navigate('/weather-stations/setup?pair=1')}>
              <Plus className="h-4 w-4 mr-1" /> Add station
            </Button>
          </div>
        )}

        {/* ================================================================ */}
        {/*  Station rolling summary + history charts (only if paired)       */}
        {/* ================================================================ */}
        {obs && obs.summary && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-lg">Station rolling summary</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <StatTile icon={Umbrella} label="Rain 24h" value={num(obs.summary.rain_24h_mm, 1)} unit="mm" color="indigo" />
                <StatTile icon={Umbrella} label="Rain 7d" value={num(obs.summary.rain_7d_mm, 1)} unit="mm" color="indigo" />
                <StatTile icon={Umbrella} label="Rain 30d" value={num(obs.summary.rain_30d_mm, 1)} unit="mm" color="indigo" />
                <StatTile icon={Thermometer} label="Min 7d" value={num(obs.summary.min_temp_7d, 1)} unit="°C" color="blue" />
                <StatTile icon={Thermometer} label="Max 7d" value={num(obs.summary.max_temp_7d, 1)} unit="°C" color="rose" />
                <StatTile icon={Wind} label="Peak gust 7d" value={num(obs.summary.peak_gust_7d, 0)} unit="km/h" color="slate" />
              </div>
            </CardContent>
          </Card>
        )}

        {hasStation && (
          <div className="flex items-center gap-1">
            <span className="text-sm text-neutral-500 mr-1">History:</span>
            {(['24h', '7d', '30d'] as TimeRange[]).map((r) => (
              <button
                key={r}
                onClick={() => setTimeRange(r)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${timeRange === r ? 'bg-sky-600 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}
              >
                {r}
              </button>
            ))}
          </div>
        )}

        {obs && obs.observations.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-1"><CardTitle className="text-base">Station temperature</CardTitle></CardHeader>
              <CardContent><div className="h-48"><canvas ref={tempChartRef} /></div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-1"><CardTitle className="text-base">Station rainfall</CardTitle></CardHeader>
              <CardContent><div className="h-48"><canvas ref={rainChartRef} /></div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-1"><CardTitle className="text-base">Station wind</CardTitle></CardHeader>
              <CardContent><div className="h-48"><canvas ref={windChartRef} /></div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-1"><CardTitle className="text-base">Station solar radiation</CardTitle></CardHeader>
              <CardContent><div className="h-48"><canvas ref={solarChartRef} /></div></CardContent>
            </Card>
          </div>
        )}

        {/* Soil probes */}
        {hasSoil && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-lg flex items-center gap-2"><Sprout className="h-5 w-5 text-green-600" /> Soil probe</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {latest!.soil_temp_c_1 != null && <StatTile icon={Thermometer} label="Soil temp" value={num(latest!.soil_temp_c_1, 1)} unit="°C" color="green" />}
                {latest!.soil_moisture_1 != null && <StatTile icon={Droplets} label="Soil moisture" value={num(latest!.soil_moisture_1, 0)} unit="%" color="green" />}
                {latest!.leaf_wetness_1 != null && <StatTile icon={Leaf} label="Leaf wetness" value={num(latest!.leaf_wetness_1, 0)} color="green" />}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Air quality */}
        {hasAir && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-lg">Air quality</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {latest!.pm25 != null && <StatTile icon={Gauge} label="PM2.5" value={num(latest!.pm25, 1)} unit="µg/m³" color="rose" />}
                {latest!.co2 != null && <StatTile icon={Gauge} label="CO₂" value={num(latest!.co2, 0)} unit="ppm" color="rose" />}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}

// -----------------------------------------------------------------------------
// HourlyMiniChart — inline Chart.js component for 24h forecast visualisation
// -----------------------------------------------------------------------------
function HourlyMiniChart({
  label, color, data, yMin,
}: {
  label: string;
  color: string;
  data: { t: string; v: Numeric }[];
  yMin?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    if (chartRef.current) chartRef.current.destroy();

    const isMobile = window.innerWidth < 640;
    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        // Open-Meteo returns hourly times already in the forecast location's
        // local timezone (YYYY-MM-DDTHH:mm, no Z), so we can safely slice
        // the hour straight out of the string without a Date parse.
        labels: data.map((d) => `${d.t.slice(11, 13)}:00`),
        datasets: [{
          label,
          data: data.map((d) => toNum(d.v)),
          borderColor: color,
          backgroundColor: `${color}22`,
          fill: true,
          tension: 0.3,
          pointRadius: 0,
          borderWidth: 2,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: { ticks: { display: !isMobile, maxTicksLimit: 6, autoSkip: true } },
          y: yMin != null ? { min: yMin } : { beginAtZero: false },
        },
        plugins: { legend: { display: false } },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [label, color, data, yMin]);

  return (
    <div>
      <div className="text-xs font-semibold text-neutral-600 mb-1">{label}</div>
      <div className="h-32"><canvas ref={canvasRef} /></div>
    </div>
  );
}
